import React from 'react';
import { ArrowRight, Zap, Clock, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/mockData';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language].hero;

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0a0c0f] via-[#141619] to-[#1a1d23]">
      {/* Circuitry Pattern Background */}
      <div className="absolute inset-0 circuit-pattern opacity-30"></div>
      
      {/* Animated Gold Accents */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-[100px] animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#5A6F8F]/10 rounded-full blur-[100px] animate-pulse animation-delay-2000"></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 lg:px-8 py-20 lg:py-0">
        <div className="text-center">
          {/* Premium Badge */}
          <div className="inline-flex items-center space-x-2 mb-8 px-4 py-2 bg-gradient-to-r from-[#1a1d23] to-[#141619] rounded-full border border-[#D4AF37]/30 shadow-lg">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37] font-semibold">
              {t.aiPowered}
            </span>
          </div>

          {/* Main Heading with Metallic Gold */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 leading-tight">
            <span className="text-gold-metallic inline-block">
              {t.title.split(' ')[0]} {t.title.split(' ')[1]}
            </span>
            <br />
            <span className="text-white">
              {t.title.split(' ').slice(2).join(' ')}
            </span>
          </h1>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal text-[#5A6F8F] mb-8">
            {t.subtitle}
          </h2>

          {/* Description */}
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed">
            {t.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-16">
            <button
              onClick={scrollToContact}
              className="group relative px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] rounded-full font-bold text-sm uppercase tracking-widest hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300 overflow-hidden btn-premium"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <span>{t.cta}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </button>

            <div className="flex items-center space-x-2 px-6 py-3 bg-[#1a1d23]/80 backdrop-blur-sm rounded-full border border-[#5A6F8F]/30">
              <Clock className="w-5 h-5 text-[#5A6F8F]" />
              <span className="text-sm font-medium text-gray-300">
                {t.weekDelivery}
              </span>
            </div>
          </div>

          {/* Stats with Premium Styling */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: '100+', label: language === 'en' ? 'Projects' : language === 'es' ? 'Proyectos' : 'Projekte' },
              { value: '50+', label: language === 'en' ? 'Clients' : language === 'es' ? 'Clientes' : 'Kunden' },
              { value: '5 Days', label: language === 'en' ? 'Delivery' : language === 'es' ? 'Entrega' : 'Lieferung' },
              { value: '98%', label: language === 'en' ? 'Satisfaction' : language === 'es' ? 'Satisfacción' : 'Zufriedenheit' },
            ].map((stat, index) => (
              <div
                key={index}
                className="group p-6 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border border-[#2a3441] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-105 relative overflow-hidden"
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/10 group-hover:to-transparent transition-all duration-300"></div>
                
                <div className="relative z-10">
                  <div className="text-3xl md:text-4xl font-bold text-gold-metallic mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400 uppercase tracking-wide">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Grid Lines */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent"></div>
    </section>
  );
};

export default Hero;
